import * as Yup from 'yup';
import React, { useState, useMemo } from 'react';
import Button from 'components/Button';
import { useVerifyAdmin, useJwtToken } from 'helper';
import { inviteUser } from '../service';

import { Formik, Form } from 'formik';
import CompanyDropdown from 'components/HGForm/CompanyDropdown';
import getValidationSchema from 'components/HGForm/formValidations';
import { addSpaceBeforeEveryCapitalLetter } from 'components/HGForm/helpers';
import HGFormInput from 'components/HGForm/HGFormInput';
import HGFormSubmitBtn from 'components/HGForm/HGFormSubmitBtn';
import HGError from 'components/HGForm/HGError';
import HGSuccessCard from 'components/HGSuccessCard';
import { Auth } from 'aws-amplify';
import { logActivity } from 'helper';
import { useToast } from 'components/shadcn/hooks/use-toast';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogClose,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from 'components/shadcn/ui/dialog';
import { DocumentDuplicateIcon } from '@heroicons/react/24/outline';

const InviteInstallerModal = () => {
    const accessToken = useJwtToken();
    const isAdmin = useVerifyAdmin();
    const user = Auth?.user;
    const [isLoading, setIsLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const { toast } = useToast();
    const initialFieldsValues = useMemo(
        () => ({
            installerEmail: '',
            company: {
                ID: user?.attributes['custom:company_id'] ?? '',
            },
        }),
        [user]
    );
    const fieldKeysArray = Object.keys(initialFieldsValues);

    const validationSchema = useMemo(() => {
        const schemaShape = fieldKeysArray.reduce((acc, key) => {
            const schema = getValidationSchema(key);
            const fieldLabel = addSpaceBeforeEveryCapitalLetter(key);
            acc[key] = schema.required(`${fieldLabel} is required`);
            return acc;
        }, {});
        return Yup.object().shape(schemaShape);
    }, [fieldKeysArray]);

    const onClose = () => {
        setSuccess(false);
        setErrorMessage(null);
    };

    const handleCopyClick = (companyId) => {
        const textToCopy = `
            Dear Homegrid Partner,
        
            You have been invited to join our company as an installer. Please use our company ID below when signing up for an installer account.
        
            ${companyId}
        
            Thank you,
            Lithion Battery
            `;
        navigator.clipboard
            .writeText(textToCopy)
            .then(() =>
                toast({
                    title: 'Invite email Copied',
                })
            )
            .catch((err) => console.error('Failed to copy text: ', err));
    };

    const sendInvitationEmail = async (values) => {
        setIsLoading(true);
        const { installerEmail, company } = values;
        if (!installerEmail || !company?.ID) {
            setIsLoading(false);
            return;
        }

        logActivity(
            'User',
            'Info',
            'Invite Installer',
            `Invite email sent to installer ${installerEmail} with company ID ${company?.ID}`,
            {
                before: values,
                after: null,
            }
        );

        try {
            await inviteUser(
                {
                    UserType: 'Installer',
                    CompanyID: company.ID,
                    InstallerEmailAddress: installerEmail,
                },
                accessToken
            );
            setSuccess(true);
        } catch (error) {
            console.error('Error sending invite email to installer:', error);
            setErrorMessage(error.message || 'Failed to send invite');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Dialog>
            <DialogTrigger asChild>
                <Button>Invite</Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle className="text-foreground">
                        Invite Installer
                    </DialogTitle>
                    <DialogDescription className="text-secondary">
                        This is a preview of the installer invitation email.
                        click send to send it directly from here or click to
                        copy the message and send it from a separate email.
                    </DialogDescription>
                </DialogHeader>
                {success ? (
                    <HGSuccessCard>
                        <div>
                            <p>The invitation email has been sent</p>
                            <DialogClose asChild>
                                <Button className="mt-3" onClick={onClose}>
                                    Close
                                </Button>
                            </DialogClose>
                        </div>
                    </HGSuccessCard>
                ) : (
                    <Formik
                        initialValues={initialFieldsValues}
                        validationSchema={validationSchema}
                        onSubmit={sendInvitationEmail}
                        validateOnBlur={false}
                    >
                        {({ values }) => (
                            <Form>
                                <div className="mb-3 flex flex-col justify-center gap-2 md:flex-row">
                                    <HGFormInput
                                        name="installerEmail"
                                        type="email"
                                        placeholder="Email"
                                        className="w-full"
                                    />
                                    {isAdmin && <CompanyDropdown />}
                                </div>
                                <div className="relative mb-3 rounded-md border bg-slate-100 p-2">
                                    <p className="text-sm text-foreground">
                                        Dear Homegrid Partner,
                                        <br />
                                        <br />
                                        You have been invited to join our
                                        company as an installer. Please use our
                                        company ID below when signing up for an
                                        installer account.
                                        <br />
                                        <br />
                                        <strong>
                                            {values?.company?.ID ||
                                                '[Please Select a Company]'}
                                        </strong>
                                        <br />
                                        <br />
                                        Thank you,
                                        <br />
                                        Lithion Battery
                                    </p>
                                    <Button
                                        className="absolute right-0 top-0 text-foreground disabled:text-secondary"
                                        variant="ghost"
                                        size="icon"
                                        disabled={!values.company?.ID}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleCopyClick(values.company?.ID);
                                        }}
                                    >
                                        <DocumentDuplicateIcon />
                                    </Button>
                                </div>
                                {errorMessage && (
                                    <HGError errorMessage={errorMessage} />
                                )}
                                <DialogFooter>
                                    <DialogClose asChild>
                                        <Button
                                            className="w-full"
                                            onClick={onClose}
                                        >
                                            Close
                                        </Button>
                                    </DialogClose>
                                    <HGFormSubmitBtn
                                        className="w-full"
                                        loading={isLoading}
                                        disabled={
                                            !values.installerEmail ||
                                            !values.company?.ID
                                        }
                                    >
                                        Send
                                    </HGFormSubmitBtn>
                                </DialogFooter>
                            </Form>
                        )}
                    </Formik>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default InviteInstallerModal;
